/**
 * Métodos de pago del turno (diálogo de la liquidación).
 *
 * Porta la pestaña *Métodos de pago* del módulo anterior: los cobros del turno
 * agrupados por método (`obtener_resumen_metodos_pago_turno`), el detalle de las
 * ventas de cada método (`obtener_detalles_metodo_pago_turno`) y la **corrección**
 * del cobro de una venta (`actualizar_metodo_pago_venta`): permite corregir el
 * método y el monto principal o secundario sin alterar el total de la venta.
 *
 * Reglas conservadas:
 * - El detalle muestra el **importe de esa parte** del cobro, no el total de la
 *   venta: una venta puede ir repartida entre dos métodos.
 * - Con el turno **verificado** (o rechazado) la corrección queda bloqueada, como
 *   el candado del panel anterior; la API lo comprueba también.
 * - La acción sólo se ofrece con el permiso `liquidaciones.metodos_pago.editar`.
 */

import { useQueryClient } from '@tanstack/react-query';
import { Check, Lock, Pencil, X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/http/api-error';
import {
  cn,
  formatCurrency,
  formatDateTimeValue,
  formatNumber,
  formatPaymentMethodName,
} from '@/lib/utils';

import {
  reportesApi,
  useCatalogosMetodosPago,
  useDetallesMetodoPago,
  useMetodosPagoDelTurno,
  type CobroDelTurno,
  type PermisosLiquidacion,
  type TurnoConCuadreOpcional,
} from '../reportes.api';

interface Props {
  /** Turno abierto o cerrado; con el cierre a ciegas llega **sin** `resumen_financiero`. */
  turno: TurnoConCuadreOpcional | null;
  permisos?: PermisosLiquidacion;
  onClose: () => void;
  /** Refresca el día cuando un cobro cambia (el cuadre depende del método). */
  onCambio: () => Promise<void> | void;
}

/** Etiqueta del tipo de cobro. */
function etiquetaTipo(tipo: string) {
  return tipo === 'SECUNDARIO' ? 'Secundario' : 'Principal';
}

export function TurnoMetodosPagoDialog({ turno, permisos, onClose, onCambio }: Props) {
  const cliente = useQueryClient();
  const turnoId = turno?.id ?? null;

  const [metodoElegido, setMetodoElegido] = useState<string | null>(null);
  const [cobroEditando, setCobroEditando] = useState<CobroDelTurno | null>(null);
  const [nuevoMetodo, setNuevoMetodo] = useState('');
  const [tipoPago, setTipoPago] = useState<'PRIMARIO' | 'SECUNDARIO'>('PRIMARIO');
  const [montoPago, setMontoPago] = useState('');
  const [referencia, setReferencia] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const resumen = useMetodosPagoDelTurno(turnoId);
  const metodos = resumen.data ?? [];
  const catalogos = useCatalogosMetodosPago();
  const detalle = useDetallesMetodoPago(turnoId, metodoElegido);

  // El primer método con cobros es el que se muestra al abrir.
  useEffect(() => {
    if (!turnoId) {
      setMetodoElegido(null);
      return;
    }

    if (metodos.length === 0) return;

    setMetodoElegido((actual) =>
      actual && metodos.some((metodo) => metodo.metodo_pago === actual)
        ? actual
        : (metodos[0]?.metodo_pago ?? null),
    );
  }, [turnoId, metodos]);

  const limpiarFormulario = () => {
    setCobroEditando(null);
    setNuevoMetodo('');
    setMontoPago('');
    setReferencia('');
    setAviso(null);
  };

  const abrirCorreccion = (cobro: CobroDelTurno) => {
    setCobroEditando(cobro);
    setNuevoMetodo(
      cobro.tipo_pago === 'SECUNDARIO'
        ? cobro.metodo_pago_secundario
        : cobro.metodo_pago_primario || detalle.data?.metodo_pago || '',
    );
    setTipoPago(cobro.tipo_pago);
    setMontoPago(String(cobro.monto));
    setReferencia(cobro.referencia ?? '');
    setAviso(null);
  };

  const cambiarPartePago = (tipo: 'PRIMARIO' | 'SECUNDARIO') => {
    if (!cobroEditando) return;

    setTipoPago(tipo);
    if (tipo === 'PRIMARIO') {
      setNuevoMetodo(cobroEditando.metodo_pago_primario || detalle.data?.metodo_pago || '');
      setMontoPago(String(cobroEditando.monto_pago_primario));
      setReferencia(cobroEditando.referencia_pago_primario);
    } else {
      setNuevoMetodo(cobroEditando.metodo_pago_secundario);
      setMontoPago(
        cobroEditando.monto_pago_secundario > 0 ? String(cobroEditando.monto_pago_secundario) : '',
      );
      setReferencia(cobroEditando.referencia_pago_secundario);
    }
  };

  const guardar = async () => {
    if (!cobroEditando) return;

    if (!nuevoMetodo) {
      setAviso('Elige el método de pago.');
      return;
    }

    const monto = Number(montoPago);
    const montoComplementario = Number((cobroEditando.total_venta - monto).toFixed(2));
    if (
      !Number.isFinite(monto) ||
      monto <= 0 ||
      montoComplementario < 0 ||
      (tipoPago === 'SECUNDARIO' && montoComplementario <= 0)
    ) {
      setAviso('Ingresa un monto mayor que cero y que no supere el total de la venta.');
      return;
    }
    if (
      tipoPago === 'PRIMARIO' &&
      cobroEditando.monto_pago_secundario <= 0 &&
      Math.round(monto * 100) !== Math.round(cobroEditando.total_venta * 100)
    ) {
      setAviso('El pago principal debe cubrir el total si no se agrega un pago secundario.');
      return;
    }

    setAviso(null);
    setOcupado(true);

    try {
      await reportesApi.actualizarMetodoPago({
        venta_id: cobroEditando.venta_id,
        tipo_pago: tipoPago,
        nuevo_metodo_pago: nuevoMetodo,
        monto,
        referencia,
      });

      await cliente.invalidateQueries({ queryKey: ['reportes', 'turnos', 'metodos-pago'] });
      await cliente.invalidateQueries({
        queryKey: ['reportes', 'turnos', 'metodos-pago-detalle', turnoId],
      });
      await onCambio();
      limpiarFormulario();
    } catch (fallo) {
      setAviso(fallo instanceof Error ? fallo.message : 'No se pudo cambiar el método de pago.');
    } finally {
      setOcupado(false);
    }
  };

  const puedeEditar = Boolean(permisos?.metodos_pago_editar);
  const bloqueado = Boolean(detalle.data?.turno_verificado);
  const ventas = detalle.data?.ventas ?? [];
  const totalCobrado = metodos.reduce((total, metodo) => total + metodo.total_monto, 0);
  // Los pagos con tarjeta del cuadre; con el **cierre a ciegas** el resumen no llega.
  const pagosNoEfectivo = turno?.resumen_financiero?.metodos_pago_no_efectivo ?? [];
  const montoIngresado = Number(montoPago);
  const montoPrincipalVista = tipoPago === 'PRIMARIO'
    ? montoIngresado
    : cobroEditando
      ? cobroEditando.total_venta - montoIngresado
      : 0;
  const tienePagoSecundario =
    tipoPago === 'SECUNDARIO' ||
    Boolean(cobroEditando?.metodo_pago_secundario && cobroEditando.monto_pago_secundario > 0);
  const montoSecundarioVista = tipoPago === 'SECUNDARIO'
    ? montoIngresado
    : tienePagoSecundario && cobroEditando
      ? cobroEditando.total_venta - montoIngresado
      : 0;

  return (
    <Dialog
      open={turno !== null}
      onClose={onClose}
      title="Métodos de pago del turno"
      description={
        turno
          ? `Turno #${turno.id} · ${turno.first_name ?? ''} ${turno.last_name ?? ''}`.trim()
          : undefined
      }
      size="xl"
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          {metodos.map((metodo) => (
            <button
              key={metodo.metodo_pago}
              type="button"
              aria-pressed={metodoElegido === metodo.metodo_pago}
              onClick={() => {
                setMetodoElegido(metodo.metodo_pago);
                limpiarFormulario();
              }}
              className={cn(
                'rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                metodoElegido === metodo.metodo_pago
                  ? 'border-brand-300 bg-brand-50 text-brand-700'
                  : 'border-ink-200 bg-white text-ink-600 hover:border-brand-200 hover:text-brand-700',
              )}
            >
              {formatPaymentMethodName(metodo.metodo_pago)} · {formatCurrency(metodo.total_monto)}
            </button>
          ))}

          <span className="ml-auto text-xs text-ink-500">
            Cobros del turno: {formatCurrency(totalCobrado)} ·{' '}
            {formatNumber(metodos.reduce((total, metodo) => total + metodo.total_transacciones, 0))}{' '}
            transacción(es).
          </span>
        </div>
        {pagosNoEfectivo.length > 0 ? (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ink-600">
            <span className="font-semibold text-ink-800">Cobros no efectivo del cuadre</span>
            {pagosNoEfectivo.map((metodo) => (
              <span key={metodo.metodo}>
                {formatPaymentMethodName(metodo.metodo)}: {formatCurrency(metodo.monto)}
              </span>
            ))}
          </div>
        ) : null}

        {aviso ? (
          <Alert variant="warning" title="Revisa el dato">
            {aviso}
          </Alert>
        ) : null}

        {resumen.isError || detalle.isError ? (
          <Alert variant="error" title="No se pudieron cargar los métodos de pago">
            {resumen.error instanceof ApiError
              ? resumen.error.message
              : detalle.error instanceof ApiError
                ? detalle.error.message
                : 'Inténtalo de nuevo en unos segundos.'}
          </Alert>
        ) : null}

        {bloqueado ? (
          <Alert variant="info" title="Turno verificado">
            El turno está verificado, así que sus cobros no se pueden cambiar. Deshaz la verificación
            si necesitas corregir un método de pago.
          </Alert>
        ) : null}

        {resumen.isLoading ? <Skeleton className="h-24 w-full" /> : null}

        {!resumen.isLoading && metodos.length === 0 ? (
          <p className="text-sm text-ink-500">
            Este turno no tiene cobros con métodos distintos del efectivo.
          </p>
        ) : null}

        {metodos.length > 0 ? (
          <>
            <div className="grid gap-2 sm:grid-cols-4">
              {[
                { etiqueta: `Total ${detalle.data?.metodo_pago ?? ''}`, valor: formatCurrency(detalle.data?.total_monto ?? 0) },
                { etiqueta: 'Transacciones', valor: formatNumber(detalle.data?.total_transacciones ?? 0) },
                { etiqueta: 'Promedio', valor: formatCurrency(detalle.data?.promedio_por_transaccion ?? 0) },
                { etiqueta: 'Tipo', valor: detalle.data?.tipo_metodo || '—' },
              ].map((tarjeta) => (
                <div key={tarjeta.etiqueta} className="rounded-card border border-ink-100 bg-white p-3">
                  <p className="text-[11px] text-ink-500 uppercase">{tarjeta.etiqueta}</p>
                  <p className="text-sm font-semibold text-ink-900">{tarjeta.valor}</p>
                </div>
              ))}
            </div>

            {cobroEditando ? (
              <div className="space-y-3 rounded-card border border-brand-200 bg-brand-50/40 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-ink-800">
                    Corregir el cobro de la venta #{cobroEditando.venta_id}
                    {cobroEditando.serie_comprobante ? ` · ${cobroEditando.serie_comprobante}` : ''}
                  </p>
                  <Badge variant="outline">{formatCurrency(cobroEditando.monto)}</Badge>
                </div>

                <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  <label className="flex flex-col gap-1.5 text-sm">
                    <span className="font-medium text-ink-700">Parte del cobro</span>
                    <Select
                      value={tipoPago}
                      onChange={(evento) => cambiarPartePago(evento.target.value as 'PRIMARIO' | 'SECUNDARIO')}
                    >
                      <option value="PRIMARIO">Principal</option>
                      <option value="SECUNDARIO">Secundario</option>
                    </Select>
                  </label>

                  <Input
                    label={tipoPago === 'PRIMARIO' ? 'Monto principal' : 'Monto secundario'}
                    type="number"
                    min="0.01"
                    max={cobroEditando.total_venta}
                    step="0.01"
                    value={montoPago}
                    onChange={(evento) => setMontoPago(evento.target.value)}
                    placeholder="0.00"
                    required
                  />

                  <label className="flex flex-col gap-1.5 text-sm">
                    <span className="font-medium text-ink-700">Método de pago</span>
                    <Select value={nuevoMetodo} onChange={(evento) => setNuevoMetodo(evento.target.value)}>
                      <option value="">Elige un método…</option>
                      {(catalogos.data?.metodos ?? []).map((metodo) => (
                        <option key={metodo.id} value={metodo.nombre}>
                          {metodo.nombre}
                          {metodo.tipo ? ` (${metodo.tipo})` : ''}
                        </option>
                      ))}
                    </Select>
                  </label>

                  <Input
                    label="Referencia"
                    value={referencia}
                    onChange={(evento) => setReferencia(evento.target.value)}
                    placeholder="Voucher, operación, etc."
                  />
                </div>

                <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-ink-600">
                  <span>
                    Principal: {formatCurrency(Number.isFinite(montoPrincipalVista) ? montoPrincipalVista : 0)}
                  </span>
                  {tienePagoSecundario ? (
                    <span>
                      Secundario: {formatCurrency(Number.isFinite(montoSecundarioVista) ? montoSecundarioVista : 0)}
                    </span>
                  ) : null}
                  <span className="font-medium text-ink-800">
                    Total venta: {formatCurrency(cobroEditando.total_venta)}
                  </span>
                </div>
                {tipoPago === 'PRIMARIO' &&
                Math.round(montoIngresado * 100) === Math.round(cobroEditando.total_venta * 100) &&
                cobroEditando.monto_pago_secundario > 0 ? (
                  <p className="text-xs text-ink-500">
                    El principal cubre toda la venta; el pago secundario quedará en S/ 0.00 y se
                    quitará de la venta.
                  </p>
                ) : null}

                <div className="flex flex-wrap gap-2">
                  <Button
                    loading={ocupado}
                    onClick={() => void guardar()}
                    leftIcon={<Check className="size-4" aria-hidden="true" />}
                  >
                    Guardar
                  </Button>
                  <Button variant="outline" onClick={limpiarFormulario} leftIcon={<X className="size-4" aria-hidden="true" />}>
                    Cancelar
                  </Button>
                  <p className="ml-auto self-center text-xs text-ink-500">
                    El método cambia en la venta, así que se recalcula el cuadre del turno.
                  </p>
                </div>
              </div>
            ) : null}

            {detalle.isLoading ? <Skeleton className="h-40 w-full" /> : null}

            {detalle.data && ventas.length === 0 ? (
              <p className="text-sm text-ink-500">Este método no tiene transacciones en el turno.</p>
            ) : null}

            {ventas.length > 0 ? (
              <div className="max-h-[26rem] overflow-auto rounded-card border border-ink-100">
                <table className="w-full border-collapse text-[13px]">
                  <thead className="sticky top-0 bg-ink-50">
                    <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="px-3 py-2">Cliente</th>
                      <th className="px-3 py-2 text-right">Monto</th>
                      <th className="px-3 py-2">Referencia</th>
                      <th className="px-3 py-2">Comprobante</th>
                      <th className="px-3 py-2">Parte</th>
                      <th className="px-3 py-2">Estado</th>
                      <th className="px-3 py-2">Fecha</th>
                      <th className="px-3 py-2 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ventas.map((cobro) => (
                      <tr
                        key={`${cobro.venta_id}-${cobro.tipo_pago}`}
                        className={cn(
                          'border-t border-ink-100',
                          cobroEditando?.venta_id === cobro.venta_id &&
                            cobroEditando?.tipo_pago === cobro.tipo_pago
                            ? 'bg-brand-50/60'
                            : '',
                        )}
                      >
                        <td className="px-3 py-2">
                          <span className="font-medium text-ink-800">{cobro.cliente || '—'}</span>
                          {cobro.placa ? (
                            <span className="ml-1 text-xs text-ink-500">({cobro.placa})</span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-ink-900">
                          {formatCurrency(cobro.monto)}
                        </td>
                        <td className="px-3 py-2 text-ink-600">
                          {cobro.referencia || '—'}
                        </td>
                        <td className="px-3 py-2 text-ink-600">{cobro.serie_comprobante || '—'}</td>
                        <td className="px-3 py-2">
                          <Badge variant={cobro.tipo_pago === 'PRIMARIO' ? 'brand' : 'outline'}>
                            {etiquetaTipo(cobro.tipo_pago)}
                          </Badge>
                        </td>
                        <td className="px-3 py-2">
                          <Badge variant={cobro.estado_api === 'Aceptado' ? 'success' : 'warning'}>
                            {cobro.estado_api || '—'}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-xs text-ink-500">
                          {formatDateTimeValue(cobro.fecha)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {bloqueado ? (
                            <span className="inline-flex items-center gap-1 text-xs text-ink-400">
                              <Lock className="size-3.5" aria-hidden="true" />
                              Bloqueado
                            </span>
                          ) : puedeEditar ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => abrirCorreccion(cobro)}
                              leftIcon={<Pencil className="size-3.5" aria-hidden="true" />}
                            >
                              Corregir
                            </Button>
                          ) : (
                            <span className="text-xs text-ink-400">Sin permiso</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        ) : null}

        <p className="text-xs text-ink-500">
          El detalle muestra el importe cobrado con ese método. Si una venta se repartió entre dos
          métodos, aparece una fila por cada parte y se puede corregir la que haga falta.
        </p>
      </div>
    </Dialog>
  );
}
